import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Typography,
  makeStyles
} from '@material-ui/core';
import ReceiptIcon from '@material-ui/icons/Receipt';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import LaunchIcon from '@material-ui/icons/Launch';
import LanguageIcon from '@material-ui/icons/Language';
import CheckIcon from '@material-ui/icons/Check';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { fetchEntitlements } from '../../store/actions/auth';

let stripePromise;

const getStripePromise = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_default');
  }
  return stripePromise;
};

const SUBSCRIPTION_PLANS = [
  {
    id: 'stage0',
    stage: 'Stage 0 · Free Entry',
    tagline: 'Match + Digital Intake',
    price: 'DKK 49',
    priceUnit: '/ per patient lead',
    badge: null,
    accentColor: '#FF7F27',
    features: [
      'Branded intake page + booking URL',
      'Digital intake',
      'Red flag screening',
      'Auto-drafted intake journal note',
      'Patient Dashboard',
      'Works alongside any EHR',
      'No monthly commitment',
      'You sign up - we generate leads',
    ],
  },
  {
    id: 'stage1',
    stage: 'Stage 1 · Subscription',
    tagline: 'Clinical Workflow',
    price: 'DKK 300–400',
    priceUnit: '/ seat / mo',
    badge: '★ THE CORE',
    accentColor: '#FF7F27',
    features: [
      'Branded intake page + booking URL',
      'Digital intake',
      'Red flag screening',
      'Auto-drafted intake journal note',
      'Diagnosis-linked templates and workflows',
      'Smart action-triggered auto-journaling',
      'KPI and feedback based auto follow-up journal note',
      'Clinician-controlled adaptive exercise programs',
      'Remote monitoring + follow-up insights',
      'Integrated video consultations',
      'Free Patient app',
      'Home exercise delivery',
      'Patient education',
      'Remote monitoring',
      'Secure chat',
      'Calendar & Booking',
      'Billing & Bookkeeping',
      'Stripe payments, subscriptions and auto-pay',
    ],
  },
  {
    id: 'stage2',
    stage: 'Stage 2 · Full Platform',
    tagline: 'Complete Clinic OS',
    price: 'DKK 500–600',
    priceUnit: '/ seat / mo',
    badge: 'THE DESTINATION',
    accentColor: '#FF7F27',
    features: [
      'Everything in Stage 0+1',
      'Staff admin',
      'Analytics dashboard',
      'EAN billing to insurances',
      'Automated accounting and bookkeeping via Appelsin integration',
      'MedCom epicrisis to GPs',
      'MitID patient login',
      'White-label option',
      'Optional Corti ambient scribe add-on',
    ],
  },
];

const PLAN_LABELS = {
  stage0: 'Stage 0 · Free Entry',
  stage1: 'Stage 1 · Subscription',
  stage2: 'Stage 2 · Full Platform',
  free: 'Stage 0 · Free Entry',
  starter: 'Stage 1 · Subscription',
  professional: 'Stage 2 · Full Platform',
  enterprise: 'Stage 2 · Full Platform',
};

const PLAN_ID_ALIASES = {
  free: 'stage0',
  starter: 'stage1',
  professional: 'stage2',
  enterprise: 'stage2',
};

const normalizePlanId = (planId) => PLAN_ID_ALIASES[planId] || planId || 'stage0';

const getPlanLabel = (planId) => PLAN_LABELS[normalizePlanId(planId)] || planId || 'Stage 0 · Free Entry';

const getPlanById = (planId) => SUBSCRIPTION_PLANS.find((plan) => plan.id === normalizePlanId(planId));

const getPlanPriceLabel = (planId) => {
  const plan = getPlanById(planId);
  if (!plan) {
    return 'DKK 0';
  }
  return `${plan.price}${plan.priceUnit}`;
};

const SELECTED_PLAN_COLOR = '#002D55';

const translations = {
  en: {
    modalTitle: 'Add Payment Method',
    infoText: 'Set up your clinic payment details securely with Stripe. Once saved, your active SaaS subscription can renew automatically until cancelled.',
    securePayment: 'Secure payment processing by Stripe',
    saveButton: 'Save Payment Method',
    cancelButton: 'Cancel',
    loadingError: 'Failed to load payment form',
  },
  da: {
    modalTitle: 'Tilfoj Betalingsmetode',
    infoText: 'Konfigurer klinikkens betalingsoplysninger sikkert med Stripe. Nar de er gemt, kan aktive SaaS-abonnementer fornyes automatisk indtil annullering.',
    securePayment: 'Sikker betalingsbehandling af Stripe',
    saveButton: 'Gem Betalingsmetode',
    cancelButton: 'Annuller',
    loadingError: 'Kunne ikke indlaese betalingsformular',
  }
};

const useStyles = makeStyles(() => ({
  root: {
    background: '#f7faff',
    borderRadius: 14,
    boxShadow: '0 6px 32px rgba(0,45,85,0.18)',
    marginTop: 0,
  },
  tabsWrapper: {
    backgroundColor: '#f7faff',
    borderRadius: 12,
    marginBottom: '0.6%',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,45,85,0.12)',
  },
  tabsControlRoot: {
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 44px)',
    '& .MuiTabs-flexContainer': {
      height: '100%',
      alignItems: 'center',
    },
  },
  tabsIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: 18,
    bottom: 0,
  },
  tabRoot: {
    fontWeight: 600,
    fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
    textTransform: 'uppercase',
    color: '#002D55',
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    minWidth: 'clamp(60px, 6.9vw, 100px)',
    padding: '0 clamp(4px, 0.7vw, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    '&.Mui-selected': {
      color: '#002D55',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  cardContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  tabContent: {
    padding: '0 16px 16px',
  },
  planCard: {
    border: '1px solid #d7e2ef',
    borderRadius: 14,
    padding: '14px 14px 12px',
    backgroundColor: '#fff',
    height: '100%',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    userSelect: 'none',
    '&:hover': {
      borderColor: '#fb741a',
      boxShadow: '0 4px 16px rgba(0,45,85,0.08)',
    },
  },
  planCardSelected: {
    boxShadow: '0 4px 18px rgba(0,45,85,0.12)',
  },
  planStage: {
    color: '#999',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  planTagline: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: '0.95rem',
    lineHeight: 1.25,
    marginBottom: 8,
  },
  planPriceRow: {
    marginBottom: 10,
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
  },
  planPrice: {
    fontWeight: 800,
    fontSize: '1.25rem',
  },
  planPriceUnit: {
    color: '#999',
    fontSize: '0.72rem',
  },
  planFeatureRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 5,
    marginBottom: 4,
  },
  planFeatureText: {
    color: '#555',
    fontSize: '0.75rem',
    lineHeight: 1.45,
  },
  planSelectedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 10,
    borderTop: '1px solid #eee',
    paddingTop: 8,
  },
  planSelectedText: {
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  planBadge: {
    position: 'absolute',
    top: -11,
    right: 10,
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    borderRadius: 4,
    padding: '2px 7px',
    letterSpacing: 0.8,
    whiteSpace: 'nowrap',
  },
  sectionTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 16,
    marginBottom: 10,
  },
  bodyText: {
    color: '#5f6c7b',
    fontSize: 13,
  },
  statusChip: {
    backgroundColor: '#E0E0E0',
    color: '#666666',
    marginTop: 8,
  },
  statusChipActive: {
    backgroundColor: '#d4edda',
    color: '#155724',
    marginTop: 8,
  },
  statCard: {
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #d7e2ef',
    padding: 12,
  },
  primaryButton: {
    color: '#FFFFFF',
    backgroundColor: '#002D55',
    borderRadius: 18,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#fb741a',
      boxShadow: 'none',
    }
  },
  secondaryButton: {
    color: '#002D55',
    backgroundColor: '#e6eaf0',
    borderRadius: 18,
    boxShadow: 'none',
    fontWeight: 'bold',
    '&:hover': {
      color: '#fb741a',
      backgroundColor: '#e6eaf0',
      boxShadow: 'none',
    },
  },
  paymentShell: {
    borderRadius: 10,
    border: '1px solid #d9e5f1',
    padding: 14,
    backgroundColor: '#fff',
  },
  invoiceRow: {
    border: '1px solid #d7e2ef',
    borderRadius: 10,
    background: '#fff',
    padding: 12,
    marginBottom: 10,
  },
  modalInfo: {
    backgroundColor: '#f5f9ff',
    border: '1px solid #e3f2fd',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
  },
  closeIconButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 12,
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#fb741a',
      border: 'none',
      boxShadow: 'none',
    }
  }
}));

const SetupForm = ({ onSuccess, onCancel, language }) => {
  const t = translations[language];
  const stripe = useStripe();
  const elements = useElements();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || loading) {
      return;
    }

    setLoading(true);
    setError('');
    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message || 'Failed to save payment method');
      setLoading(false);
      return;
    }

    setLoading(false);
    onSuccess(result.setupIntent);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box className={classes.paymentShell}>
        <PaymentElement />
      </Box>
      {error ? (
        <Typography style={{ color: '#d32f2f', marginTop: 8, fontSize: 13 }}>
          {error}
        </Typography>
      ) : null}
      <Box style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
          className={classes.primaryButton}
          style={{ flex: 1 }}
        >
          {loading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : t.saveButton}
        </Button>
        <Button onClick={onCancel} disabled={loading} className={classes.closeIconButton}>
          {t.cancelButton}
        </Button>
      </Box>
    </form>
  );
};

SetupForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  language: PropTypes.oneOf(['en', 'da']).isRequired,
};

const AddPaymentMethodModal = ({ open, onClose, onSuccess, token, backendBaseUrl }) => {
  const classes = useStyles();
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('en');
  const t = translations[language];

  useEffect(() => {
    if (!open || !token) {
      return;
    }

    let ignore = false;
    const fetchIntent = async () => {
      try {
        setError('');
        setClientSecret('');
        const response = await fetch(`${backendBaseUrl}/api/crm/provider/subscription/setup-intent/`, {
          method: 'POST',
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || t.loadingError);
        }
        if (!ignore) {
          setClientSecret(data.client_secret || '');
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || t.loadingError);
        }
      }
    };

    fetchIntent();
    return () => {
      ignore = true;
    };
  }, [open, token, backendBaseUrl, t.loadingError]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 18 } }}>
      <DialogTitle style={{ color: '#002D55' }}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <svg width="60" height="25" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fill="#635BFF"/>
            </svg>
            <Box style={{ width: 1, height: 24, backgroundColor: '#d0d0d0' }} />
            <span style={{ color: '#002D55', fontWeight: 600 }}>{t.modalTitle}</span>
          </Box>
          <Button
            size="small"
            onClick={() => setLanguage(language === 'en' ? 'da' : 'en')}
            style={{ minWidth: 60, padding: '4px 8px' }}
          >
            <LanguageIcon style={{ fontSize: 18, marginRight: 4 }} />
            {language === 'en' ? 'DA' : 'EN'}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingBottom: 24 }}>
        <Box className={classes.modalInfo}>
          <Typography variant="body2" style={{ color: '#555', lineHeight: 1.6 }}>
            {t.infoText}
          </Typography>
          <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
            {t.securePayment}
          </Typography>
        </Box>

        {error ? (
          <Typography style={{ color: '#d32f2f', marginBottom: 12 }}>
            {error}
          </Typography>
        ) : null}

        {clientSecret ? (
          <Elements key={language} stripe={getStripePromise()} options={{ clientSecret, locale: language }}>
            <SetupForm onCancel={onClose} onSuccess={onSuccess} language={language} />
          </Elements>
        ) : (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

AddPaymentMethodModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  token: PropTypes.string,
  backendBaseUrl: PropTypes.string.isRequired,
};

const ClinicSubscriptionSettings = ({ className, ...rest }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('stage0');
  const [autoRenew, setAutoRenew] = useState(true);
  const [paymentPreference, setPaymentPreference] = useState('invoice');

  const hasPaymentMethod = useMemo(() => Boolean(subscription?.has_payment_method), [subscription]);

  const fetchSubscription = async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/subscription/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription');
      }

      setSubscription(data);
      const fetchedPlan = normalizePlanId(data?.subscription?.plan || 'stage0');
      setSelectedPlan(fetchedPlan);
      const renewEnabled = data?.subscription?.auto_renew !== false;
      setAutoRenew(renewEnabled);
      setPaymentPreference(renewEnabled ? 'auto' : 'invoice');
    } catch (err) {
      setError(err.message || 'Failed to fetch subscription');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingHistory = async () => {
    if (!token) {
      return;
    }
    setHistoryLoading(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/subscription/billing-history/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch billing history');
      }
      setBillingHistory(Array.isArray(data.history) ? data.history : []);
    } catch (_err) {
      setBillingHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    fetchBillingHistory();
  }, [token]);

  const formatMoney = (value, currency = 'EUR') => {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return `${currency} 0.00`;
    }
    return `${currency} ${amount.toFixed(2)}`;
  };

  const formatDate = (isoDate) => {
    if (!isoDate) {
      return 'N/A';
    }
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleDateString();
  };

  const handlePreferenceChange = (pref) => {
    setPaymentPreference(pref);
    setAutoRenew(pref === 'auto');
  };

  const handleSaveSubscription = async () => {
    if (!token) {
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/subscription/`, {
        method: 'PUT',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: selectedPlan,
          auto_renew: autoRenew,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save subscription');
      }

      setSuccess(data.warning ? `Subscription settings updated successfully. ${data.warning}` : 'Subscription settings updated successfully.');
      await dispatch(fetchEntitlements());
      await fetchSubscription();
      await fetchBillingHistory();
    } catch (err) {
      setError(err.message || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPortal = async () => {
    if (!token) {
      return;
    }
    setError('');
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/subscription/portal-session/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          return_url: window.location.href,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      if (data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(err.message || 'Failed to open billing portal');
    }
  };

  const handlePaymentMethodSaved = async (setupIntent) => {
    if (!token) {
      return;
    }
    try {
      const response = await fetch(`${backendBaseUrl}/api/crm/provider/subscription/set-default-payment-method/`, {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ setup_intent_id: setupIntent.id }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save default payment method');
      }
      setSuccess('Payment method saved successfully.');
      setModalOpen(false);
      await fetchSubscription();
    } catch (err) {
      setError(err.message || 'Failed to save payment method');
    }
  };

  const currentSubscription = subscription?.subscription;
  const currentPlan = normalizePlanId(currentSubscription?.plan || 'stage0');
  const currentPlanLabel = getPlanLabel(currentPlan);

  return (
    <form
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Card className={clsx(classes.root, className)}>
        <Paper elevation={0} className={classes.tabsWrapper}>
          <Tabs
            value={0}
            variant="scrollable"
            scrollButtons="auto"
            classes={{ root: classes.tabsControlRoot, indicator: classes.tabsIndicator }}
          >
            <Tab label="Subscription" className={classes.tabRoot} />
          </Tabs>
        </Paper>
        <CardContent className={classes.cardContent}>
          <Box className={classes.tabContent}>
            <Box style={{ padding: '12px 0' }}>
              <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 4 }}>
                Choose your plan
              </Typography>
              <Typography variant="body2" style={{ color: '#666', marginBottom: 16 }}>
                Select the tier that fits your clinic. You can upgrade at any time from your dashboard.
              </Typography>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <Typography className={classes.bodyText}>
                  Manage your clinic plan, card on file, and Stripe billing history.
                </Typography>
                <Chip
                  label={`Current plan: ${currentPlanLabel}`}
                  style={{ backgroundColor: '#e9f2fb', color: '#002D55', fontWeight: 700 }}
                />
              </Box>

              {selectedPlan && (() => {
                const plan = SUBSCRIPTION_PLANS.find((item) => item.id === selectedPlan);
                return plan ? (
                  <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: `${plan.accentColor}18`, border: `1px solid ${plan.accentColor}55`, borderRadius: 20, padding: '3px 12px', marginBottom: 16 }}>
                    <CheckCircleIcon style={{ fontSize: 14, color: plan.accentColor }} />
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: plan.accentColor }}>
                      {plan.stage} · {plan.price}{plan.priceUnit}
                    </Typography>
                  </Box>
                ) : null;
              })()}

            {loading ? (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <Grid item xs={12} sm={6} md={4} key={plan.id}>
                    {(() => {
                      const isSelected = selectedPlan === plan.id;
                      const accentColor = isSelected ? SELECTED_PLAN_COLOR : plan.accentColor;
                      return (
                    <Box
                      className={clsx(classes.planCard, selectedPlan === plan.id ? classes.planCardSelected : null)}
                      onClick={() => setSelectedPlan(plan.id)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(event) => {
                        if (event.key === 'Enter') {
                          setSelectedPlan(plan.id);
                        }
                      }}
                      style={{
                        border: `2px solid ${accentColor}`,
                        backgroundColor: selectedPlan === plan.id
                          ? '#F0F4FA'
                          : '#fff',
                      }}
                    >
                      {plan.badge ? (
                        <Box className={classes.planBadge} style={{ backgroundColor: accentColor }}>
                          {plan.badge}
                        </Box>
                      ) : null}
                      <Typography className={classes.planStage}>
                        {plan.stage}
                      </Typography>
                      <Typography className={classes.planTagline}>
                        {plan.tagline}
                      </Typography>
                      <Box className={classes.planPriceRow}>
                        <Typography className={classes.planPrice} style={{ color: accentColor }}>
                          {plan.price}
                        </Typography>
                        <Typography className={classes.planPriceUnit}>
                          {plan.priceUnit}
                        </Typography>
                      </Box>
                      <Box style={{ flex: 1 }}>
                        {plan.features.map((feature) => (
                          <Box key={feature} className={classes.planFeatureRow}>
                            <CheckIcon style={{ fontSize: 13, color: accentColor, marginTop: 3, flexShrink: 0 }} />
                            <Typography className={classes.planFeatureText}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {selectedPlan === plan.id ? (
                        <Box className={classes.planSelectedRow}>
                          <CheckCircleIcon style={{ color: accentColor, fontSize: 15 }} />
                          <Typography className={classes.planSelectedText} style={{ color: accentColor }}>
                            Selected
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                      );
                    })()}
                  </Grid>
                ))}
              </Grid>
            )}

            <Divider style={{ marginTop: 24, marginBottom: 24 }} />

            <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 16 }}>
              Payment Method
            </Typography>
            <Typography className={classes.bodyText}>
              Payments are securely handled by Stripe, including credit/debit cards, Apple Pay, and MobilePay.
            </Typography>
            <Chip
              label={hasPaymentMethod ? 'Card on file' : 'No card on file'}
              className={hasPaymentMethod ? classes.statusChipActive : classes.statusChip}
              size="small"
            />

            <Divider style={{ marginTop: 24, marginBottom: 4 }} />
            <List>
              <ListItem>
                <ListItemIcon>
                  <CreditCardIcon style={{ color: '#002D55' }} />
                </ListItemIcon>
                <ListItemText
                  primary={hasPaymentMethod ? 'Payment method saved' : 'Add a payment method'}
                  secondary="Cards are stored securely in Stripe for future subscription invoices."
                  primaryTypographyProps={{ style: { color: '#002D55', fontWeight: 600 } }}
                  secondaryTypographyProps={{ style: { color: '#666666' } }}
                />
              </ListItem>
            </List>
            <Button
              className={classes.primaryButton}
              variant="contained"
              fullWidth
              style={{ marginTop: 8 }}
              onClick={() => setModalOpen(true)}
            >
              {hasPaymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
            </Button>

            <Divider style={{ marginTop: 24, marginBottom: 24 }} />

            <Typography className={classes.sectionTitle} style={{ fontSize: 18, marginBottom: 16 }}>
              Payment Preference
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                row
                value={paymentPreference}
                onChange={(e) => handlePreferenceChange(e.target.value)}
              >
                <FormControlLabel
                  value="auto"
                  control={<Radio style={{ color: paymentPreference === 'auto' ? '#FF7F27' : '#002D55' }} />}
                  label="Auto-renew (charge saved card when subscription is due)"
                />
                <FormControlLabel
                  value="invoice"
                  control={<Radio style={{ color: paymentPreference === 'invoice' ? '#FF7F27' : '#002D55' }} />}
                  label="Manual renewal (no automatic card charge)"
                />
              </RadioGroup>
            </FormControl>

            <Grid container spacing={2} style={{ marginTop: 2 }}>
              <Grid item xs={12} md={4}>
                <Box className={classes.statCard}>
                  <Typography style={{ color: '#5f6c7b', fontSize: 12 }}>Subscription status</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, marginTop: 4, textTransform: 'capitalize' }}>
                    {currentSubscription?.status || 'trial'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className={classes.statCard}>
                  <Typography style={{ color: '#5f6c7b', fontSize: 12 }}>Next billing date</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, marginTop: 4 }}>
                    {formatDate(currentSubscription?.next_billing_date)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box className={classes.statCard}>
                  <Typography style={{ color: '#5f6c7b', fontSize: 12 }}>Total paid</Typography>
                  <Typography style={{ color: '#002D55', fontWeight: 700, marginTop: 4 }}>
                    {formatMoney(currentSubscription?.total_paid || 0)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {subscription?.default_card ? (
              <Box style={{ marginTop: 12 }}>
                <Chip
                  icon={<CreditCardIcon />}
                  label={`${subscription.default_card.brand || 'Card'} ending in ${subscription.default_card.last4 || '----'}`}
                  style={{ backgroundColor: '#edf7ed', color: '#1f5f3f', fontWeight: 600 }}
                />
              </Box>
            ) : null}

            {error ? <Typography style={{ color: '#d32f2f', marginTop: 14 }}>{error}</Typography> : null}
            {success ? <Typography style={{ color: '#2e7d32', marginTop: 14 }}>{success}</Typography> : null}

            <Divider style={{ marginTop: 16, marginBottom: 16 }} />

            <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button className={classes.primaryButton} variant="contained" disabled={saving || (selectedPlan !== 'stage0' && !hasPaymentMethod)} onClick={handleSaveSubscription}>
                {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Save Subscription'}
              </Button>
              <Button className={classes.secondaryButton} variant="contained" onClick={handleOpenPortal} startIcon={<LaunchIcon />}>
                Open Stripe Billing Portal
              </Button>
            </Box>

            {selectedPlan !== 'stage0' && !hasPaymentMethod ? (
              <Typography style={{ color: '#d32f2f', marginTop: 10, fontSize: 13 }}>
                Add a payment method before activating a paid subscription.
              </Typography>
            ) : null}

            <Divider style={{ marginTop: 22, marginBottom: 12 }} />

            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <ReceiptIcon style={{ color: '#002D55' }} />
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>Billing History</Typography>
            </Box>

            {historyLoading ? (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                <CircularProgress size={24} />
              </Box>
            ) : billingHistory.length === 0 ? (
              <Typography className={classes.bodyText}>No Stripe billing records yet.</Typography>
            ) : (
              <Box>
                {billingHistory.map((item) => (
                  <Box key={item.id || item.number} className={classes.invoiceRow}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Typography style={{ color: '#002D55', fontWeight: 700 }}>{item.number || item.id}</Typography>
                        <Typography className={classes.bodyText}>{formatDate(item.created_at)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography className={classes.bodyText}>Status</Typography>
                        <Chip
                          size="small"
                          label={(item.status || 'unknown').toUpperCase()}
                          style={{ backgroundColor: item.status === 'paid' ? '#d4edda' : '#fff4e5', color: '#002D55' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography className={classes.bodyText}>Amount paid</Typography>
                        <Typography style={{ color: '#002D55', fontWeight: 700 }}>
                          {formatMoney(item.amount_paid, item.currency)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          {item.hosted_invoice_url ? (
                            <Button
                              size="small"
                              className={classes.secondaryButton}
                              onClick={() => window.open(item.hosted_invoice_url, '_blank', 'noopener,noreferrer')}
                            >
                              View
                            </Button>
                          ) : null}
                          {item.invoice_pdf ? (
                            <Button
                              size="small"
                              className={classes.secondaryButton}
                              onClick={() => window.open(item.invoice_pdf, '_blank', 'noopener,noreferrer')}
                            >
                              PDF
                            </Button>
                          ) : null}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          </Box>
        </CardContent>
      </Card>

      <AddPaymentMethodModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handlePaymentMethodSaved}
        token={token}
        backendBaseUrl={backendBaseUrl}
      />
    </form>
  );
};

ClinicSubscriptionSettings.propTypes = {
  className: PropTypes.string
};

export default ClinicSubscriptionSettings;